import Config from '../utils/config'
import QueryParams from '../utils/QueryParams'
import React, { useRef, useEffect, useMemo } from 'react'
import { AgGridReact } from 'ag-grid-react';
import { InboundPartnr } from '../types/CommonTypes';
import { ModuleRegistry, AllCommunityModule, themeAlpine, ColDef, GridApi} from 'ag-grid-community';
import { useAssignment } from '../hooks/useAssignment';

export type PartNrTableProps = {
    partnrs: InboundPartnr[];
}

export default function PartNrTable({ partnrs }: PartNrTableProps): JSX.Element{
    const devMode = QueryParams.devMode;
    const isCreo = QueryParams.useCreoConnector;
    ModuleRegistry.registerModules([AllCommunityModule]);
    const { assignedCondition, assignedConditionType, markUnsavedChanges, assignedMaterial, assignedTreatment } = useAssignment();
    const conditionCode = assignedCondition?.code ?? null;
    const gridApiRef = useRef<GridApi | null>(null);

    useEffect(() => {
        gridApiRef.current?.refreshCells({ force: true });
    }, [assignedMaterial?.adb, assignedTreatment?.adb]);

    const columnDefs: ColDef[] = useMemo(() => [
        { headerName: 'Partnr', field: 'partnr', flex: 70, editable: !!devMode } as ColDef,
        { headerName: 'bmId', field: 'bmId', flex: 40, editable: !!devMode, hide: !devMode } as ColDef,
        { headerName: 'Material', field: 'bmName', flex: 100, editable: !!devMode, valueFormatter: params => {
            const name = params.value ?? '';
            if (!name) return '';
            const adb = assignedMaterial?.adb;
            return adb ? `${adb}_${name}` : name;
        }} as ColDef,
        { headerName: 'BM Density', field: 'bmDensity', flex: 60, editable: !!devMode, hide: !devMode, valueFormatter: params => params.value ? `${params.value} kg/dm³` : '' } as ColDef,
        { headerName: 'stId', field: 'stMaterialId', flex: 40, editable: !!devMode, hide: !devMode } as ColDef,
        { headerName: 'Surface Treatment', field: 'stName', flex: 100, editable: !!devMode, valueFormatter: params => {
            const name = params.value ?? '';
            if (!name) return '';
            const adb = assignedTreatment?.adb;
            return adb ? `${adb}_${name}` : name;
        }} as ColDef,
        { headerName: 'ST Density', field: 'stDensity', flex: 60, editable: !!devMode, hide: !devMode, valueFormatter: params => params.value ? `${params.value} kg/dm³` : '' } as ColDef,
        { headerName: 'Coverage', field: 'coverage', flex: 40, hide: !isCreo, editable: isCreo, cellClass: 'editable', cellEditor: 'agNumberCellEditor', valueFormatter: params => params.value ? `${parseFloat(params.value).toFixed(2)}%` : '', valueGetter: x=>x?.data?.coverage ? x.data.coverage * 100 : '', valueSetter: x=>{let nv = parseFloat(String(x.newValue).replace('%','').trim()); if(Number.isNaN(nv)) return false; x.data.coverage = nv/100.0; return true} } as ColDef,
        { headerName: 'Thickness', field: 'thickness', flex: 50, editable: !!devMode, valueFormatter: params => params.value ? `${params.value} µm` : '' } as ColDef,
        { headerName: assignedConditionType === 'color' ? 'Color Code' : assignedConditionType === 'dimension' ? 'Dimension Code' : 'Condition', flex: 70, hide: !conditionCode, valueGetter: () => conditionCode ?? '' } as ColDef,
    ], [conditionCode, assignedConditionType, isCreo, assignedMaterial?.adb, assignedTreatment?.adb]);
    return (
          <AgGridReact
            onGridReady={(params) => { gridApiRef.current = params.api; }}
            rowData={partnrs}
            columnDefs={columnDefs}
            onCellValueChanged={() => markUnsavedChanges()}
            loadThemeGoogleFonts={true}
            domLayout='autoHeight'
            stopEditingWhenCellsLoseFocus={true}
            theme={themeAlpine.withParams({
              spacing: "2px",
              columnBorder:"1px solid #adb8c2f6",
              headerColumnBorder: "1px solid #adb8c2f6",
              wrapperBorder:"1px solid #adb8c2f6",
              wrapperBorderRadius: "8px",
              rowBorder: "1px solid #adb8c2f6"
            })}
            selectionColumnDef={{width: 30, headerName: ''}}
          />
    );
};